<script setup>
// SiteUp: deixa o usuário escolher quais itens da barra lateral ele quer ver.
// As escolhas são por usuário (uiSettings.sidebar_hidden_menu_items) e a barra
// (Sidebar.vue) lê essa mesma lista pra esconder os itens em tempo real.
//
// A lista mostra só o que o usuário REALMENTE vê na barra: usamos a mesma
// checagem da barra (usePolicy.shouldShow, lendo o meta da rota representativa
// de cada seção) + a feature flag da conta. Então itens escondidos por cargo
// (ex.: Relatórios/Configurações só pra admin) ou por feature desligada nem
// aparecem como opção — como o Lucas pediu ("conforme aparece pro usuário").
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { SIDEBAR_MENU_SECTIONS } from 'dashboard/components-next/sidebar/menuSections';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const router = useRouter();
const { uiSettings, updateUISettings } = useUISettings();
const { shouldShow, isFeatureFlagEnabled } = usePolicy();

const hiddenItems = computed(() => {
  const hidden = uiSettings.value?.sidebar_hidden_menu_items;
  return Array.isArray(hidden) ? hidden : [];
});

// Lê o meta da rota sem precisar resolver params (mesmo truque do provider da
// barra: findRouteByName). Params (accountId, etc.) não importam pro meta.
const findRouteMeta = name => {
  if (!name) return {};
  const route = router.getRoutes().find(r => r.name === name);
  return route?.meta || {};
};

const isSectionAllowed = section => {
  if (section.featureFlag && !isFeatureFlagEnabled(section.featureFlag)) {
    return false;
  }
  const meta = findRouteMeta(section.routeName);
  return shouldShow(
    meta.featureFlag || '',
    meta.permissions ?? [],
    meta.installationTypes ?? []
  );
};

const sections = computed(() =>
  SIDEBAR_MENU_SECTIONS.filter(isSectionAllowed)
);

const labelFor = section => section.label || t(section.labelKey);

const isVisible = section => !hiddenItems.value.includes(section.name);

const visibleCount = computed(
  () => sections.value.filter(section => isVisible(section)).length
);

// Guarda nomes ESCONDIDOS (não os visíveis): assim o padrão "sem config" já é
// tudo visível, e qualquer item novo do sistema também nasce visível.
const setVisibility = (section, visible) => {
  const next = new Set(hiddenItems.value);
  if (visible) {
    next.delete(section.name);
  } else {
    next.add(section.name);
  }
  updateUISettings({ sidebar_hidden_menu_items: Array.from(next) });
};

const showAll = () => {
  if (!hiddenItems.value.length) return;
  updateUISettings({ sidebar_hidden_menu_items: [] });
};
</script>

<template>
  <div class="flex flex-col w-full gap-1">
    <div class="flex items-center justify-between pb-2">
      <span class="text-sm text-n-slate-11">
        {{ visibleCount }}/{{ sections.length }} visíveis
      </span>
      <button
        type="button"
        class="text-sm text-n-brand disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="!hiddenItems.length"
        @click="showAll"
      >
        Mostrar todos
      </button>
    </div>
    <div
      v-for="section in sections"
      :key="section.name"
      class="flex items-center justify-between py-2 border-t border-n-weak first:border-t-0"
    >
      <span class="text-sm text-n-slate-12">{{ labelFor(section) }}</span>
      <ToggleSwitch
        :model-value="isVisible(section)"
        @update:model-value="value => setVisibility(section, value)"
      />
    </div>
  </div>
</template>
